import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BoltIcon from '@mui/icons-material/Bolt';
import ReactFlow, { Background, Controls } from 'reactflow';
import 'reactflow/dist/style.css';
import { enqueueSnackbar } from 'notistack';

import {
  ActionNode,
  AuthNode,
  BranchNode,
  ConditionNode,
  DefaultNode,
  ErrorNode,
  FlowNode,
  LoopNode,
  MicrosoftNode,
  ResponseNode,
  TriggerNode,
  ViolationNode,
} from '@src/components';
import { colors } from '@src/styles';
import { AutomationsApi } from '@src/services/automations/automations-api';
import { getSource, TSources } from '../utils/sources';
import { BlockDictionary, calculateNodePositions } from '../utils/flowLayout';

const monoFontFamily = "'JetBrains Mono', monospace";
const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

const nodeTypes = {
  ACTION: ActionNode,
  AUTH: AuthNode,
  BRANCH: BranchNode,
  CONDITION: ConditionNode,
  DEFAULT: DefaultNode,
  ERROR: ErrorNode,
  FLOW: FlowNode,
  LOOP: LoopNode,
  MICROSOFT: MicrosoftNode,
  RESPONSE: ResponseNode,
  TRIGGER: TriggerNode,
  VIOLATION: ViolationNode,
};

const TYPE_LEGEND: Record<string, { label: string; color: string }> = {
  TRIGGER: { label: 'Gatilho', color: colors.primary.yellow },
  ACTION: { label: 'Ação', color: colors.brand.textStrong },
  AUTH: { label: 'Autenticação', color: colors.brand.textSecondary },
  BRANCH: { label: 'Ramificação', color: colors.primary.blue },
  CONDITION: { label: 'Condição', color: colors.primary.blue },
  LOOP: { label: 'Repetição', color: colors.status.pending },
  FLOW: { label: 'Subfluxo', color: colors.brand.textStrong },
  MICROSOFT: { label: 'Microsoft', color: colors.chart.integration },
  RESPONSE: { label: 'Resposta', color: colors.status.concluded },
  VIOLATION: { label: 'Violação', color: colors.status.rejected },
  ERROR: { label: 'Erro', color: colors.status.rejected },
  DEFAULT: { label: 'Bloco', color: colors.brand.textMuted },
};

export interface FlowDiagramPanelProps {
  path: string;
  dataSource: TSources;
  port: number;
  onClose: () => void;
}

export const FlowDiagramPanel = ({ path, dataSource, port, onClose }: FlowDiagramPanelProps) => {
  const automationsApi = useMemo(() => new AutomationsApi(undefined, getSource(dataSource, port)), [dataSource, port]);
  const isLive = dataSource === 'localhost';

  const {
    data: flow,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['getFlow', path, dataSource, port],
    queryFn: () => automationsApi.v1.flows.get({ params: { path } }),
    refetchInterval: isLive ? 1000 : false,
  });

  const { nodes, edges } = useMemo(
    () => (flow ? calculateNodePositions(flow as BlockDictionary) : { nodes: [], edges: [] }),
    [flow],
  );

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    nodes.forEach((node) => {
      const type = node.type ?? 'DEFAULT';
      counts[type] = (counts[type] ?? 0) + 1;
    });
    return Object.entries(counts).sort(([a], [b]) => {
      const order = Object.keys(TYPE_LEGEND);
      return order.indexOf(a) - order.indexOf(b);
    });
  }, [nodes]);

  const segments = path.split('/').filter(Boolean);
  const flowName = segments[segments.length - 1] ?? path;

  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(path);
      enqueueSnackbar('Caminho copiado', { variant: 'success', autoHideDuration: 1000 });
    } catch (error) {
      console.error('Failed to copy path:', error);
    }
  };

  return (
    <Box
      component="section"
      aria-label={`Fluxo ${flowName}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: '14px',
        border: `1px solid ${colors.brand.line}`,
        backgroundColor: colors.background.primary,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          padding: '12px 14px',
          borderBottom: `1px solid ${colors.brand.line}`,
          backgroundColor: colors.brand.surface,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.primary.yellow,
            backgroundColor: 'rgba(255, 160, 21, 0.12)',
          }}
        >
          <BoltIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              component="h2"
              noWrap
              sx={{ fontFamily: monoFontFamily, fontSize: '15px', fontWeight: 500, color: colors.primary.white }}
            >
              {flowName}
            </Typography>
            {isLive && (
              <Box
                sx={{
                  flexShrink: 0,
                  fontFamily: monoFontFamily,
                  fontSize: '9.5px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: colors.status.concluded,
                  border: `1px solid ${colors.status.concluded}55`,
                  backgroundColor: `${colors.status.concluded}14`,
                  borderRadius: '999px',
                  padding: '2px 8px',
                }}
              >
                ao vivo
              </Box>
            )}
          </Box>
          <Typography noWrap sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
            {path}
          </Typography>
        </Box>
        <Tooltip title="Copiar caminho" arrow>
          <IconButton
            onClick={handleCopyPath}
            aria-label="Copiar caminho do fluxo"
            sx={{
              width: 32,
              height: 32,
              color: colors.brand.textSecondary,
              '&:hover': { color: colors.primary.white },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fechar fluxo" arrow>
          <IconButton
            onClick={onClose}
            aria-label="Fechar fluxo"
            sx={{
              width: 32,
              height: 32,
              color: colors.brand.textSecondary,
              '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {typeCounts.length > 0 && (
        <Box
          aria-label="Legenda dos blocos"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px 14px',
            padding: '8px 14px',
            borderBottom: `1px solid ${colors.brand.line}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: monoFontFamily,
              fontSize: '10px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: colors.brand.textMuted,
            }}
          >
            {nodes.length} blocos
          </Typography>
          {typeCounts.map(([type, count]) => {
            const legend = TYPE_LEGEND[type] ?? TYPE_LEGEND.DEFAULT;
            return (
              <Box key={type} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '3px', backgroundColor: legend.color }} />
                <Typography sx={{ fontSize: '12px', color: colors.brand.textSecondary }}>
                  {legend.label} · {count}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          '& .react-flow__controls': {
            boxShadow: 'none',
            border: `1px solid ${colors.brand.line}`,
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .react-flow__controls-button': {
            backgroundColor: colors.brand.surface,
            borderBottom: `1px solid ${colors.brand.line}`,
            fill: colors.brand.textSecondary,
            '&:hover': { backgroundColor: '#1A2126', fill: colors.primary.white },
          },
          '& .react-flow__attribution': { backgroundColor: 'transparent', opacity: 0.4 },
          '& .react-flow__attribution a': { color: colors.brand.textMuted },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <CircularProgress size={18} sx={{ color: colors.primary.yellow }} />
            <Typography sx={{ fontFamily: titleFontFamily, fontSize: '13px', color: colors.brand.textSecondary }}>
              Carregando fluxo…
            </Typography>
          </Box>
        ) : isError || nodes.length === 0 ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 3,
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.primary.white }}>
                {isError ? 'Não foi possível carregar o fluxo' : 'Fluxo sem blocos'}
              </Typography>
              <Typography sx={{ marginTop: 0.5, fontSize: '12.5px', color: colors.brand.textSecondary }}>
                {isError
                  ? 'Verifique a fonte selecionada (Padrão/Localhost) e tente de novo.'
                  : 'Não há blocos a partir do startTrigger para desenhar.'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <ReactFlow
            key={path}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.2}
            nodesConnectable={false}
          >
            <Background gap={18} size={1} color="rgba(255, 255, 255, 0.07)" />
            <Controls showInteractive={false} position="bottom-left" />
          </ReactFlow>
        )}
      </Box>
    </Box>
  );
};
